import React, { useState, useEffect } from 'react';
import env from '../../environment';
import Api from '../../api/global.service';
import xlsx from 'json-as-xlsx';
import moment from 'moment';
import transformWord from '../../utils/transformWord.js';
import useEtiquetas from '../../hooks/useEtiquetas';
import insertIf from '../../utils/insertIf.js';
import { useAuth } from '../content/auth.jsx';
import BlockUi from 'react-block-ui';
import ROLES from '../../constants/Roles.js';
import { isMiel } from '../content/coop-config.jsx';

const ModalParcelasDownload = ({ isOpen, onClose, idCooperativa, idCiclo, data, etiquetas, coopConfig }) => {
  const [blocking, setBlocking] = useState(false);
  const auth = useAuth();

  // If EUDR is not enabled, automatically download simple version
  const useEudr = !!+coopConfig?.config?.usa_eudr;

  const handleSimpleDownload = React.useCallback(() => {
    setBlocking(true);

    const columns = [
      { label: 'ID SIRIO', value: 'id' },
      { label: 'CÓDIGO', value: 'codigo' },
      { label: 'PRODUCTOR', value: 'cuenta' },
      { label: 'NOMBRE ' + transformWord(etiquetas?.parcela, ['uppercase']), value: 'nombre' },
      { label: 'GEOLOCALIZADA', value: 'geoLabel' },
      { label: 'MUNICIPIO', value: 'municipio' },
      { label: 'COMUNIDAD', value: 'localidad' },
      ...insertIf(auth.user.id_rol == ROLES.ADMIN, { label: 'Altitud (msnm)', value: row => row.altitud_primer_punto != null ? +row.altitud_primer_punto : '' }),
      { label: 'SUPERFICIE (HA)', value: row => +row.superficie },
      { label: transformWord(etiquetas?.planta, ['plural', 'uppercase']) + ' TOTALES', value: row => +row.plantas },
      ...insertIf(!isMiel(), { label: 'ALERTAS POLÍGONO', value: 'alertas_poligono', maxWidth: 150 }),
      ...insertIf(+coopConfig.config?.usa_eudr, { label: 'ÁREAS NATURALES', value: 'areas_naturales', maxWidth: 150 }),
      ...insertIf(+coopConfig.config?.usa_eudr, { label: 'RIESGO EUDR preliminar', value: 'riskLabelPreliminar' }),
      ...insertIf(+coopConfig.config?.usa_eudr, { label: 'RIESGO EUDR', value: 'riskLabel' }),
      ...insertIf(+coopConfig.config?.usa_eudr && (env.appEnv === 'dev' || env.appEnv === 'local'), { label: 'JUSTIFICACIÓN (columna solo disponible en DEV para análisis)', value: 'justificacion_riesgo_eudr_user' }),
      ...insertIf(auth.user.id_rol == ROLES.ADMIN, { label: 'ELEVACIÓN (m)', value: row => row.elevation || '' }),
    ];

    const xlsData1 = [
      {
        sheet: transformWord(etiquetas?.parcela, ['plural','capitalize']),
        columns: columns,
        content: data,
      },
    ];

    const settings = {
      fileName: `${transformWord(etiquetas?.parcela, ['lowercase', 'plural'])}_${moment().format('YYYYMMDD_HHmmss')}`,
    };
    
    try {
      xlsx(xlsData1, settings);
      setBlocking(false);
      onClose();
    } catch (error) {
      console.error('Error generating Excel file:', error);
      setBlocking(false);
    }
  }, [data, etiquetas, coopConfig, onClose, auth.user.id_rol]);

  const handleAdvancedDownload = () => {
    setBlocking(true);
    
    const url = `parcelas_whisp_report/${idCooperativa}/${idCiclo}`;
    
    Api.download(url)
      .then(response => {
        // no file returned, refresh the page since backend need more time
        if (response.data.size === 0) {
          setTimeout(() => window.location.reload(), 15000);
          setBlocking(false);
          return false;
        }

        const fileName = response.headers['content-disposition'].split('filename=')[1];
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        link.click();
        setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 0);

        setBlocking(false);
        onClose();
      })
      .catch(e => {
        console.error('Error downloading advanced Excel file:', e);
        setBlocking(false);
      });
  };

  useEffect(() => {
    if (isOpen && !useEudr) {
      handleSimpleDownload();
    }
  }, [isOpen, useEudr, handleSimpleDownload]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1060,
      }}
      onClick={onClose}
    >
      <BlockUi
        message=""
        blocking={blocking}
        renderChildren={false}
        style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '10px',
          minWidth: '400px',
          textAlign: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: '20px' }}>
          <img src={env.images.icon.xls} alt="Excel Icon" style={{ marginRight: '10px' }} />
          <h4 style={{ margin: 0, color: '#333' }}>Listado de {transformWord(etiquetas?.parcela, ['plural','capitalize'])}</h4>
        </div>

        {/* Download Buttons */}
        {blocking || !useEudr ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '15px' }}>
            <div
              className="spinner-border text-warning mr-2"
              role="status"
              aria-hidden="true"
              style={{ height: 24, width: 24 }}
            />
            <span>Descargando...</span>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
            {/* Simple Download Option */}
            <div style={{ flex: 1 }}>
              <button
                onClick={handleSimpleDownload}
                style={{
                  padding: '12px 16px',
                  backgroundColor: env.colors?.primary_500 || '#789D16',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'background-color 0.2s',
                  width: '100%',
                  textAlign: 'center',
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = env.colors?.primary_300}
                onMouseOut={(e) => e.target.style.backgroundColor = env.colors?.primary_500 || '#789D16'}
              >
                Descarga Simple
              </button>
              <p style={{ fontSize: '12px', color: '#666', margin: '8px 0 0 0', textAlign: 'center' }}>
                Datos de la tabla actual en pantalla {auth?.user?.id_rol == ROLES.ADMIN ? <b> (ESTA DESCARGA INCLUYE LAS ALTITUDES DE LAS {transformWord(etiquetas?.parcela, ['plural','uppercase'])})</b> : null}
              </p>
            </div>
            
            {/* Advanced Download Option */}
            <div style={{ flex: 1 }}>
              <button
                onClick={handleAdvancedDownload}
                style={{
                  padding: '12px 16px',
                  backgroundColor: env.colors?.secondary_300,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'background-color 0.2s',
                  width: '100%',
                  textAlign: 'center',
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = env.colors?.secondary_200}
                onMouseOut={(e) => e.target.style.backgroundColor = env.colors?.secondary_300}
              >
                Descarga Detallada
              </button>
              <p style={{ fontSize: '12px', margin: '8px 0 0 0', textAlign: 'center' }}>
              Datos en pantalla más desglose de alertas y errores
              </p>
            </div>
          </div>
        )}
        
        <button
          onClick={onClose}
          disabled={blocking || !useEudr}
          style={{
            padding: '8px 16px',
            backgroundColor: '#f8f9fa',
            color: '#666',
            border: '1px solid #ddd',
            borderRadius: '5px',
            cursor: (blocking || !useEudr) ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            opacity: (blocking || !useEudr) ? 0.6 : 1,
          }}
        >
          Cancelar
        </button>
      </BlockUi>
    </div>
  );
};

export default ModalParcelasDownload;
